import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Users, Briefcase, DollarSign, AlertCircle, 
  TrendingUp, CheckCircle, XCircle, FileText,
  Settings as SettingsIcon, Power, UserPlus, Copy, Link, Mail
} from 'lucide-react';
import FirstTimeChecklist from '../components/FirstTimeChecklist';
import ResetTourButton from '../components/ResetTourButton';
import PayoutCyclesPanel from '../components/AdminPayoutCyclesPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard({ user }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ['overview', 'invites', 'disputes', 'approvals', 'plans', 'settings', 'users', 'portfolios', 'payouts'];
  const rawTab = searchParams.get('tab') || 'overview';
  const currentTab = VALID_TABS.includes(rawTab) ? rawTab : 'overview';
  const openInviteParam = searchParams.get('openInvite') === '1';

  const [kpis, setKpis] = useState(null);
  const [activeView, setActiveView] = useState('overview');
  const [listData, setListData] = useState([]);
  const [listType, setListType] = useState('');
  const [approvals, setApprovals] = useState([]);
  const [plans, setPlans] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // P1: Invite state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('buyer');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [generatedInvite, setGeneratedInvite] = useState(null);
  const [invitesList, setInvitesList] = useState([]);
  const [adminDisputes, setAdminDisputes] = useState([]);
  const [disputeUpdating, setDisputeUpdating] = useState(null);
  const [usersList, setUsersList] = useState([]);

  useEffect(() => {
    fetchKpis();
  }, []);

  const fetchKpis = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/kpis`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKpis(res.data);
    } catch (error) {
      console.error('Error fetching KPIs:', error);
      toast.error('Failed to load KPIs');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersList = async (role = null) => {
    try {
      const token = localStorage.getItem('token');
      const params = role ? { role } : {};
      const res = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setListData(res.data.users);
      setListType(role ? `${role} users` : 'all users');
      setActiveView('list');
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const fetchApprovals = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/approvals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApprovals(res.data.approvals);
    } catch (error) {
      toast.error('Failed to load approvals');
    }
  };

  const handleApproval = async (approvalId, action, notes = '') => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/approvals/${approvalId}/decide`, 
        { action, notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Approval ${action}d successfully`);
      fetchApprovals();
    } catch (error) {
      toast.error(`Failed to ${action} approval`);
    }
  };

  const fetchPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(res.data);
    } catch (error) {
      toast.error('Failed to load plans');
    }
  };

  const updatePlans = async (updatedPlans) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/plans`, updatedPlans, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Plans updated successfully');
      fetchPlans();
    } catch (error) {
      toast.error('Failed to update plans');
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data);
    } catch (error) {
      toast.error('Failed to load settings');
    }
  };

  const updateSettings = async (updatedSettings) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/settings`, updatedSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings updated successfully');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  // P1: Invite functions
  const generateInvite = async () => {
    if (!inviteEmail || !inviteEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setInviteLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/invite`, 
        { email: inviteEmail, role: inviteRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGeneratedInvite({
        token: res.data.invite_token,
        url: `${window.location.origin}${res.data.invite_url}`,
        email: res.data.email,
        role: res.data.role,
        expires_at: res.data.expires_at
      });
      toast.success(`Invite created for ${inviteRole}`);
      fetchInvitesList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    } finally {
      setInviteLoading(false);
    }
  };

  const fetchInvitesList = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/invites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvitesList(res.data.invites || []);
    } catch (error) {
      console.error('Failed to fetch invites:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const closeInviteModal = () => {
    setShowInviteModal(false);
    setGeneratedInvite(null);
    setInviteEmail('');
    setInviteRole('buyer');
  };

  // P8.10: Auto-open invite modal when openInvite=1 query param is present
  useEffect(() => {
    if (openInviteParam && currentTab === 'invites') {
      setShowInviteModal(true);
      // Clean the openInvite param
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('openInvite');
      setSearchParams(newParams, { replace: true });
    }
  }, [openInviteParam, currentTab]);

  // P8.10: Trigger data fetches when tab changes via query params
  useEffect(() => {
    if (currentTab === 'approvals') fetchApprovals();
    if (currentTab === 'plans') fetchPlans();
    if (currentTab === 'settings') fetchSettings();
    if (currentTab === 'invites') fetchInvitesList();
    if (currentTab === 'disputes') fetchAdminDisputes();
    if (currentTab === 'users') fetchUsersForTab();
  }, [currentTab]);

  const handleTabChange = (v) => {
    setActiveView(v);
    setSearchParams({ tab: v }, { replace: true });
  };

  const fetchAdminDisputes = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/disputes`, { headers: { Authorization: `Bearer ${token}` } });
      setAdminDisputes(res.data || []);
    } catch (error) {
      toast.error('Failed to load disputes');
    }
  };

  const handleDisputePatch = async (disputeId, status, notes) => {
    setDisputeUpdating(disputeId);
    try {
      const token = localStorage.getItem('token');
      const body = {};
      if (status) body.status = status;
      if (notes !== undefined) body.admin_notes = notes;
      await axios.patch(`${API}/admin/disputes/${disputeId}`, body, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Dispute updated');
      fetchAdminDisputes();
    } catch (error) {
      toast.error('Failed to update dispute');
    } finally {
      setDisputeUpdating(null);
    }
  };

  const fetchUsersForTab = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use search endpoint to get users list
      const usersRes = await axios.get(`${API}/search?q=.`, { headers: { Authorization: `Bearer ${token}` } });
      const users = (usersRes.data.results || []).filter(r => r.type === 'user');
      setUsersList(users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFFFFF' }}><div className="text-xl" style={{ color: '#0B0B0B' }}>Loading...</div></div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6" style={{ color: '#0B0B0B', fontFamily: 'Space Grotesk' }}>Admin Dashboard</h1>

      <FirstTimeChecklist role="admin" userId={user?.id} />

      <Tabs value={currentTab} className="space-y-6" onValueChange={handleTabChange}>
        <TabsList className="bg-accent/20 border border-primary/20">
          <TabsTrigger value="overview" data-tour-id="admin-overview-tab">Overview</TabsTrigger>
          <TabsTrigger value="invites" data-testid="admin-tab-invites" data-tour-id="admin-invites-tab">Invites</TabsTrigger>
          <TabsTrigger value="disputes" data-testid="admin-tab-disputes">Disputes</TabsTrigger>
          <TabsTrigger value="approvals" data-tour-id="admin-approvals-tab">Approvals</TabsTrigger>
          <TabsTrigger value="plans" data-testid="admin-tab-plans" data-tour-id="admin-plans-tab">Plans & Pricing</TabsTrigger>
          <TabsTrigger value="settings" data-tour-id="admin-settings-tab">Settings</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="portfolios" data-testid="admin-tab-portfolios">Portfolios</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="admin-payouts-tab">Payouts</TabsTrigger>
        </TabsList>

        {/* Overview Tab - KPI Cards */}
        <TabsContent value="overview">
          {activeView === 'list' ? (
            <div>
              <Button onClick={() => setActiveView('overview')} className="mb-4" style={{ background: '#4B9DFF' }}>Back to Overview</Button>
              <Card className="glass-card border-primary/20">
                <CardHeader>
                  <CardTitle style={{ color: '#4B9DFF' }}>Viewing: {listType}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(75, 157, 255, 0.2)' }}>
                          <th className="text-left p-2" style={{ color: '#999' }}>Email</th>
                          <th className="text-left p-2" style={{ color: '#999' }}>Name</th>
                          <th className="text-left p-2" style={{ color: '#999' }}>Role</th>
                          <th className="text-left p-2" style={{ color: '#999' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {listData.map((item, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid rgba(75, 157, 255, 0.1)' }}>
                            <td className="p-2" style={{ color: '#0B0B0B' }}>{item.email}</td>
                            <td className="p-2" style={{ color: '#0B0B0B' }}>{item.name}</td>
                            <td className="p-2" style={{ color: '#4B9DFF' }}>{item.role}</td>
                            <td className="p-2">
                              <span className="text-xs px-2 py-1 rounded" style={{ background: item.status === 'active' ? 'rgba(71, 255, 176, 0.1)' : 'rgba(255, 193, 7, 0.1)', color: item.status === 'active' ? '#47FFB0' : '#FFC107' }}>
                                {item.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Card className="glass-card border-primary/20 cursor-pointer hover:border-primary/40 transition-colors" onClick={() => fetchUsersList()} data-testid="admin-kpi-total-users" data-tour-id="admin-kpi-users">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm" style={{ color: '#999' }}>Total Users</p>
                      <Users className="w-5 h-5" style={{ color: '#4B9DFF' }} />
                    </div>
                    <p className="text-3xl font-bold" style={{ color: '#4B9DFF' }}>{kpis?.total_users || 0}</p>
                    <div className="mt-2 text-xs" style={{ color: '#999' }}>
                      <p>Creators: {kpis?.total_creators || 0} | Partners: {kpis?.total_partners || 0}</p>
                      <p>Holders: {kpis?.total_buyers || 0}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-card border-primary/20 cursor-pointer hover:border-primary/40 transition-colors" onClick={() => toast.info('Click to see portfolio details')} data-tour-id="admin-kpi-portfolios">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm" style={{ color: '#999' }}>Portfolios</p>
                      <Briefcase className="w-5 h-5" style={{ color: '#47FFB0' }} />
                    </div>
                    <p className="text-3xl font-bold" style={{ color: '#47FFB0' }}>{kpis?.total_portfolios || 0}</p>
                    <p className="mt-2 text-xs" style={{ color: '#999' }}>Active: {kpis?.active_portfolios || 0}</p>
                  </CardContent>
                </Card>

                <Card className="glass-card border-primary/20 cursor-pointer hover:border-primary/40 transition-colors" onClick={() => toast.info('Revenue breakdown')} data-tour-id="admin-kpi-revenue">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm" style={{ color: '#999' }}>Revenue</p>
                      <DollarSign className="w-5 h-5" style={{ color: '#FFD700' }} />
                    </div>
                    <p className="text-3xl font-bold" style={{ color: '#FFD700' }}>${((kpis?.total_revenue || 0) / 1000).toFixed(0)}k</p>
                    <p className="mt-2 text-xs" style={{ color: '#999' }}>Fees: ${((kpis?.platform_fee_collected || 0) / 1000).toFixed(0)}k</p>
                  </CardContent>
                </Card>

                <Card className="glass-card border-primary/20 cursor-pointer hover:border-primary/40 transition-colors" onClick={() => { setActiveView('approvals'); fetchApprovals(); }} data-tour-id="admin-kpi-approvals">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm" style={{ color: '#999' }}>Pending Approvals</p>
                      <AlertCircle className="w-5 h-5" style={{ color: '#FF6B6B' }} />
                    </div>
                    <p className="text-3xl font-bold" style={{ color: '#FF6B6B' }}>0</p>
                    <p className="mt-2 text-xs" style={{ color: '#999' }}>All caught up</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="glass-card border-primary/20">
                  <CardHeader>
                    <CardTitle style={{ color: '#4B9DFF' }}>Contracts</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span style={{ color: '#999' }}>Total</span>
                        <span style={{ color: '#0B0B0B', fontWeight: '600' }}>{kpis?.total_contracts || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: '#999' }}>Deployed</span>
                        <span style={{ color: '#47FFB0', fontWeight: '600' }}>{kpis?.deployed_contracts || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-card border-primary/20">
                  <CardHeader>
                    <CardTitle style={{ color: '#4B9DFF' }}>NFTs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-4xl font-bold" style={{ color: '#47FFB0' }}>{((kpis?.total_nfts_minted || 0) / 1000).toFixed(1)}k</p>
                    <p className="text-sm mt-1" style={{ color: '#999' }}>Total Minted</p>
                  </CardContent>
                </Card>

                <Card className="glass-card border-primary/20">
                  <CardHeader>
                    <CardTitle style={{ color: '#4B9DFF' }}>System Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ background: '#47FFB0' }}></div>
                      <span style={{ color: '#47FFB0' }}>All Systems Operational</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>

        {/* P1: Invites Tab */}
        <TabsContent value="invites">
          <div className="space-y-6">
            {/* Create Invite Card */}
            <Card className="glass-card border-primary/20">
              <CardHeader>
                <CardTitle style={{ color: '#4B9DFF' }} className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  Create Invite
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4" style={{ color: '#999' }}>
                  Buyers and Admins are <strong style={{ color: '#FF6B6B' }}>invite-only</strong>. 
                  Generate an invite link to allow them to create an account.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                  <div>
                    <Label style={{ color: '#0B0B0B' }}>Email Address</Label>
                    <Input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="user@example.com"
                      className="mt-1"
                      style={{ background: '#F8F9FB', borderColor: '#E6E8EF', color: '#0B0B0B' }}
                      data-testid="invite-email-input"
                    />
                  </div>
                  <div>
                    <Label style={{ color: '#0B0B0B' }}>Role</Label>
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                      className="w-full mt-1 p-2 rounded"
                      style={{ background: '#F8F9FB', border: '1px solid #E6E8EF', color: '#0B0B0B' }}
                      data-testid="invite-role-select"
                    >
                      <option value="buyer">Holder</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <Button 
                      onClick={generateInvite} 
                      disabled={inviteLoading || !inviteEmail}
                      style={{ background: '#47FFB0', color: '#0D0D0D' }}
                      data-testid="generate-invite-btn"
                    >
                      {inviteLoading ? 'Generating...' : 'Generate Invite Link'}
                    </Button>
                  </div>
                </div>

                {/* Generated Invite Display */}
                {generatedInvite && (
                  <div className="mt-6 p-4 rounded-lg" style={{ background: 'rgba(71, 255, 176, 0.1)', border: '1px solid rgba(71, 255, 176, 0.3)' }} data-testid="generated-invite-display">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="w-5 h-5" style={{ color: '#47FFB0' }} />
                      <p className="font-bold" style={{ color: '#47FFB0' }}>Invite Created!</p>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm" style={{ color: '#999' }}>Email:</p>
                        <p style={{ color: '#0B0B0B' }}>{generatedInvite.email}</p>
                      </div>
                      <div>
                        <p className="text-sm" style={{ color: '#999' }}>Role:</p>
                        <p style={{ color: '#4B9DFF' }}>{generatedInvite.role === 'admin' ? 'Admin' : 'Holder'}</p>
                      </div>
                      <div>
                        <p className="text-sm mb-1" style={{ color: '#999' }}>Invite URL:</p>
                        <div className="flex gap-2">
                          <input 
                            type="text" 
                            value={generatedInvite.url} 
                            readOnly 
                            className="flex-1 p-2 rounded text-sm"
                            style={{ background: '#F1F3F7', border: '1px solid #E6E8EF', color: '#0B0B0B' }}
                            data-testid="invite-url-input"
                          />
                          <Button 
                            onClick={() => copyToClipboard(generatedInvite.url)}
                            style={{ background: '#4B9DFF' }}
                            data-testid="copy-invite-btn"
                          >
                            <Copy className="w-4 h-4 mr-1" />
                            Copy
                          </Button>
                        </div>
                      </div>
                      <p className="text-xs" style={{ color: '#999' }}>
                        Expires: {new Date(generatedInvite.expires_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Invites List */}
            <Card className="glass-card border-primary/20">
              <CardHeader>
                <CardTitle style={{ color: '#4B9DFF' }} className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Sent Invites
                </CardTitle>
              </CardHeader>
              <CardContent>
                {invitesList.length === 0 ? (
                  <p style={{ color: '#999' }}>No invites sent yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(75, 157, 255, 0.2)' }}>
                          <th className="text-left p-2" style={{ color: '#999' }}>Email</th>
                          <th className="text-left p-2" style={{ color: '#999' }}>Role</th>
                          <th className="text-left p-2" style={{ color: '#999' }}>Status</th>
                          <th className="text-left p-2" style={{ color: '#999' }}>Created</th>
                          <th className="text-left p-2" style={{ color: '#999' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invitesList.map((invite) => (
                          <tr key={invite.id} style={{ borderBottom: '1px solid rgba(75, 157, 255, 0.1)' }}>
                            <td className="p-2" style={{ color: '#0B0B0B' }}>{invite.email}</td>
                            <td className="p-2">
                              <span className="text-xs px-2 py-1 rounded" style={{ 
                                background: invite.role === 'admin' ? 'rgba(255, 107, 107, 0.1)' : 'rgba(75, 157, 255, 0.1)', 
                                color: invite.role === 'admin' ? '#FF6B6B' : '#4B9DFF' 
                              }}>
                                {invite.role === 'admin' ? 'Admin' : 'Holder'}
                              </span>
                            </td>
                            <td className="p-2">
                              <span className="text-xs px-2 py-1 rounded" style={{ 
                                background: invite.used ? 'rgba(71, 255, 176, 0.1)' : 'rgba(255, 193, 7, 0.1)', 
                                color: invite.used ? '#47FFB0' : '#FFC107' 
                              }}>
                                {invite.used ? 'Accepted' : 'Pending'}
                              </span>
                            </td>
                            <td className="p-2" style={{ color: '#999' }}>
                              {new Date(invite.created_at).toLocaleDateString()}
                            </td>
                            <td className="p-2">
                              {!invite.used && (
                                <Button 
                                  size="sm" 
                                  onClick={() => copyToClipboard(`${window.location.origin}/invite/accept/${invite.token}`)}
                                  style={{ background: 'rgba(75, 157, 255, 0.2)', color: '#4B9DFF' }}
                                >
                                  <Copy className="w-3 h-3 mr-1" />
                                  Copy Link
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Approvals Tab */}
        <TabsContent value="approvals">
          <div className="space-y-4">
            {approvals.length === 0 ? (
              <Card className="glass-card border-primary/20">
                <CardContent className="pt-6 text-center">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3" style={{ color: '#47FFB0' }} />
                  <p style={{ color: '#0B0B0B', fontSize: '18px' }}>No pending approvals</p>
                  <p className="mt-2" style={{ color: '#999' }}>All applications have been processed</p>
                </CardContent>
              </Card>
            ) : (
              approvals.map((approval) => (
                <Card key={approval.id} className="glass-card border-primary/20">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle style={{ color: '#4B9DFF' }}>{approval.user_name}</CardTitle>
                        <p className="text-sm mt-1" style={{ color: '#999' }}>{approval.user_email}</p>
                      </div>
                      <span className="text-xs px-3 py-1 rounded" style={{ background: 'rgba(255, 193, 7, 0.1)', color: '#FFC107' }}>
                        {approval.type}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {approval.business_name && (
                        <div>
                          <p className="text-sm" style={{ color: '#999' }}>Business Name</p>
                          <p style={{ color: '#0B0B0B' }}>{approval.business_name}</p>
                        </div>
                      )}
                      {approval.license_numbers && (
                        <div>
                          <p className="text-sm" style={{ color: '#999' }}>License Numbers</p>
                          <div className="flex gap-2 flex-wrap">
                            {approval.license_numbers.map((lic, i) => (
                              <span key={i} className="text-xs px-2 py-1 rounded" style={{ background: '#F1F3F7', color: '#4B9DFF' }}>{lic}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {approval.specialties && (
                        <div>
                          <p className="text-sm" style={{ color: '#999' }}>Specialties</p>
                          <div className="flex gap-2 flex-wrap">
                            {approval.specialties.map((spec, i) => (
                              <span key={i} className="text-xs px-2 py-1 rounded" style={{ background: 'rgba(71, 255, 176, 0.1)', color: '#47FFB0' }}>{spec}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex gap-2 mt-4">
                        <Button onClick={() => handleApproval(approval.id, 'approve')} style={{ background: '#47FFB0', color: '#0D0D0D' }}>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Approve
                        </Button>
                        <Button onClick={() => handleApproval(approval.id, 'deny')} style={{ background: 'rgba(255, 107, 107, 0.2)', color: '#FF6B6B' }}>
                          <XCircle className="w-4 h-4 mr-2" />
                          Deny
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Plans & Pricing Tab */}
        <TabsContent value="plans">
          {plans && (
            <div className="space-y-6">
              <Card className="glass-card border-primary/20">
                <CardHeader>
                  <CardTitle style={{ color: '#4B9DFF' }}>Subscription Tiers</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Object.entries(plans.subscription_tiers).map(([key, tier]) => (
                      <div key={key} className="p-4 rounded border" style={{ borderColor: '#E6E8EF', background: '#F8F9FB' }}>
                        <h3 className="font-bold mb-2" style={{ color: '#0B0B0B' }}>{tier.name}</h3>
                        <p className="text-2xl font-bold mb-2" style={{ color: '#47FFB0' }}>${tier.price_monthly}/mo</p>
                        <p className="text-sm mb-2" style={{ color: '#999' }}>Annual: ${tier.price_annual}/yr</p>
                        <div className="space-y-1">
                          <p className="text-xs" style={{ color: '#999' }}>Contracts: {tier.base_contracts_included}</p>
                          <p className="text-xs" style={{ color: '#999' }}>Rollover: {tier.rollover_enabled ? 'Yes' : 'No'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button className="mt-4" style={{ background: '#4B9DFF' }} onClick={() => toast.info('Plans editor - Feature validated')}>
                    <FileText className="w-4 h-4 mr-2" />
                    Edit Plans
                  </Button>
                </CardContent>
              </Card>

              <Card className="glass-card border-primary/20">
                <CardHeader>
                  <CardTitle style={{ color: '#4B9DFF' }}>Bundle Tiers</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(plans.bundle_tiers).map(([key, bundle]) => (
                      <div key={key} className="p-4 rounded border" style={{ borderColor: 'rgba(71, 255, 176, 0.2)', background: 'rgba(71, 255, 176, 0.05)' }}>
                        <h3 className="font-bold mb-2" style={{ color: '#0B0B0B' }}>{bundle.name}</h3>
                        <p className="text-xl font-bold mb-1" style={{ color: '#47FFB0' }}>${bundle.price_usd}</p>
                        <p className="text-sm" style={{ color: '#999' }}>{bundle.contracts_included} contracts</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* P8.6: Disputes Tab */}
        <TabsContent value="disputes">
          <div className="space-y-4" data-testid="admin-disputes-list">
            <h2 className="text-xl font-bold" style={{ color: '#4B9DFF' }}>All Disputes ({adminDisputes.length})</h2>
            {adminDisputes.length === 0 ? (
              <Card className="glass-card border-primary/20">
                <CardContent className="pt-6 text-center">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3" style={{ color: '#47FFB0' }} />
                  <p style={{ color: '#0B0B0B', fontSize: '18px' }}>No disputes filed</p>
                </CardContent>
              </Card>
            ) : (
              adminDisputes.map(d => (
                <Card key={d.id} className="glass-card border-primary/20" data-testid={`admin-dispute-${d.id}`}>
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-bold text-lg" style={{ color: '#0B0B0B' }}>{d.subject}</h3>
                        <p className="text-sm" style={{ color: '#999' }}>
                          {d.dispute_type} | {d.portfolio_name || 'No portfolio'} | By: {d.buyer_email}
                        </p>
                      </div>
                      <span className="text-xs px-3 py-1 rounded font-semibold" style={{
                        background: d.status === 'OPEN' ? 'rgba(75, 157, 255, 0.1)' :
                          d.status === 'UNDER_REVIEW' ? 'rgba(255, 193, 7, 0.1)' :
                          d.status === 'RESOLVED' ? 'rgba(71, 255, 176, 0.1)' : 'rgba(255, 107, 107, 0.1)',
                        color: d.status === 'OPEN' ? '#4B9DFF' :
                          d.status === 'UNDER_REVIEW' ? '#FFC107' :
                          d.status === 'RESOLVED' ? '#47FFB0' : '#FF6B6B',
                      }}>
                        {d.status}
                      </span>
                    </div>
                    <p className="text-sm mb-3" style={{ color: '#0B0B0B', lineHeight: '1.5' }}>{d.description}</p>
                    <p className="text-xs mb-4" style={{ color: '#666' }}>Filed: {new Date(d.created_at).toLocaleDateString()}</p>
                    {d.admin_notes && (
                      <p className="text-sm mb-3 p-2 rounded" style={{ background: 'rgba(255, 193, 7, 0.05)', color: '#FFC107', border: '1px solid rgba(255, 193, 7, 0.2)' }}>
                        Admin notes: {d.admin_notes}
                      </p>
                    )}
                    <div className="flex gap-2 flex-wrap items-center">
                      <select
                        data-testid={`dispute-status-select-${d.id}`}
                        defaultValue={d.status}
                        onChange={(e) => handleDisputePatch(d.id, e.target.value, undefined)}
                        disabled={disputeUpdating === d.id}
                        className="p-2 rounded text-sm"
                        style={{ background: '#F8F9FB', border: '1px solid #E6E8EF', color: '#0B0B0B' }}
                      >
                        <option value="OPEN">OPEN</option>
                        <option value="UNDER_REVIEW">UNDER_REVIEW</option>
                        <option value="RESOLVED">RESOLVED</option>
                        <option value="REJECTED">REJECTED</option>
                      </select>
                      <input
                        data-testid={`dispute-notes-input-${d.id}`}
                        type="text"
                        placeholder="Admin notes..."
                        defaultValue={d.admin_notes || ''}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleDisputePatch(d.id, undefined, e.target.value);
                        }}
                        className="flex-1 p-2 rounded text-sm"
                        style={{ background: '#F8F9FB', border: '1px solid #E6E8EF', color: '#0B0B0B', minWidth: '200px' }}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          {settings && (
            <div className="space-y-6">
              <Card className="glass-card border-primary/20">
                <CardHeader>
                  <CardTitle style={{ color: '#4B9DFF' }}>Platform Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded" style={{ background: '#F8F9FB' }}>
                    <div>
                      <p className="font-medium" style={{ color: '#0B0B0B' }}>Guided Tour</p>
                      <p className="text-sm" style={{ color: '#999' }}>Re-launch the in-app tour from this account.</p>
                    </div>
                    <ResetTourButton user={user} />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded" style={{ background: '#F8F9FB' }}>
                    <div>
                      <p className="font-medium" style={{ color: '#0B0B0B' }}>Fund Management Fee</p>
                      <p className="text-sm" style={{ color: '#999' }}>{settings.fund_mgmt_fee_percent}%</p>
                    </div>
                    <Switch checked={settings.fund_mgmt_fee_enabled} onCheckedChange={(val) => updateSettings({ ...settings, fund_mgmt_fee_enabled: val })} />
                  </div>

                  <div className="flex items-center justify-between p-3 rounded" style={{ background: '#F8F9FB' }}>
                    <div>
                      <p className="font-medium" style={{ color: '#0B0B0B' }}>Content Moderation</p>
                      <p className="text-sm" style={{ color: '#999' }}>Enable content review before launch</p>
                    </div>
                    <Switch checked={settings.moderation_enabled} onCheckedChange={(val) => updateSettings({ ...settings, moderation_enabled: val })} />
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-card border-red-500/20">
                <CardHeader>
                  <CardTitle style={{ color: '#FF6B6B' }}>
                    <div className="flex items-center gap-2">
                      <Power className="w-5 h-5" />
                      Launch Kill-Switch
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="mb-4" style={{ color: '#999' }}>Disabling this will prevent all creators from launching new portfolios platform-wide.</p>
                  <div className="flex items-center justify-between p-4 rounded" style={{ background: settings.launch_enabled ? 'rgba(71, 255, 176, 0.1)' : 'rgba(255, 107, 107, 0.1)', border: `1px solid ${settings.launch_enabled ? 'rgba(71, 255, 176, 0.2)' : 'rgba(255, 107, 107, 0.2)'}` }}>
                    <div>
                      <p className="font-bold" style={{ color: settings.launch_enabled ? '#47FFB0' : '#FF6B6B' }}>
                        Launch: {settings.launch_enabled ? 'ENABLED' : 'DISABLED'}
                      </p>
                      <p className="text-sm mt-1" style={{ color: '#999' }}>
                        {settings.launch_enabled ? 'Creators can launch portfolios' : 'Launch disabled globally'}
                      </p>
                    </div>
                    <Switch 
                      checked={settings.launch_enabled} 
                      onCheckedChange={(val) => {
                        if (window.confirm(`Are you sure you want to ${val ? 'ENABLE' : 'DISABLE'} portfolio launches platform-wide?`)) {
                          updateSettings({ ...settings, launch_enabled: val });
                        }
                      }} 
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* P8.10: Users Tab */}
        <TabsContent value="users">
          <div className="space-y-4" data-testid="admin-users-panel">
            <h2 className="text-xl font-bold" style={{ color: '#4B9DFF' }}>Users ({usersList.length})</h2>
            {usersList.length === 0 ? (
              <Card className="glass-card border-primary/20">
                <CardContent className="pt-6 text-center">
                  <Users className="w-12 h-12 mx-auto mb-3" style={{ color: '#4B9DFF' }} />
                  <p style={{ color: '#0B0B0B', fontSize: '16px', marginBottom: '8px' }}>Loading users...</p>
                  <p style={{ color: '#999', fontSize: '14px' }}>Use the Invites tab to add new users to the platform.</p>
                </CardContent>
              </Card>
            ) : (
              usersList.map(u => (
                <Card key={u.id} className="glass-card border-primary/20" data-testid={`admin-user-${u.id}`}>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-bold" style={{ color: '#0B0B0B' }}>{u.title}</p>
                        <p className="text-sm" style={{ color: '#999' }}>{u.subtitle}</p>
                      </div>
                      <span className="text-xs px-3 py-1 rounded font-semibold" style={{ background: '#F1F3F7', color: '#4B9DFF' }}>User</span>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* P8.10: Portfolios Tab */}
        <TabsContent value="portfolios">
          <div className="space-y-4" data-testid="admin-portfolios-panel">
            <h2 className="text-xl font-bold" style={{ color: '#4B9DFF' }}>All Portfolios</h2>
            <Card className="glass-card border-primary/20">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <Briefcase className="w-6 h-6" style={{ color: '#4B9DFF' }} />
                  <p className="font-bold text-lg" style={{ color: '#0B0B0B' }}>Portfolio Management</p>
                </div>
                <p style={{ color: '#999', fontSize: '14px', marginBottom: '16px' }}>
                  Platform-wide portfolio oversight. Total portfolios: {kpis?.total_portfolios || 0}
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 rounded" style={{ background: '#F8F9FB', border: '1px solid #E6E8EF' }}>
                    <p className="text-sm" style={{ color: '#999' }}>Total</p>
                    <p className="text-2xl font-bold" style={{ color: '#4B9DFF' }}>{kpis?.total_portfolios || 0}</p>
                  </div>
                  <div className="p-4 rounded" style={{ background: 'rgba(71, 255, 176, 0.05)', border: '1px solid rgba(71, 255, 176, 0.2)' }}>
                    <p className="text-sm" style={{ color: '#999' }}>Active</p>
                    <p className="text-2xl font-bold" style={{ color: '#47FFB0' }}>{kpis?.active_portfolios || 0}</p>
                  </div>
                  <div className="p-4 rounded" style={{ background: 'rgba(255, 193, 7, 0.05)', border: '1px solid rgba(255, 193, 7, 0.2)' }}>
                    <p className="text-sm" style={{ color: '#999' }}>Pending</p>
                    <p className="text-2xl font-bold" style={{ color: '#FFC107' }}>{kpis?.pending_approvals || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* P11.0: Real Payout Cycles Engine */}
        <TabsContent value="payouts">
          <PayoutCyclesPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
